// The schedule tables in the drawing (connections, floorings, lighting, …):
// placed, laid out, picked, and their cells edited in place.
// docs/claude/connections.md.
import { shownName } from '../../shared/names';
import { type EditorLook } from '../inlineText';
import {
  type Preparation,
  type ScheduleTable,
  type TextI18n,
  type Vec2,
} from '../../shared/types';
import { connEdit } from './connectionUi';
import {
  drawingRev,
  invalidate,
  objHidden,
  objUnpickable,
  pushHistory,
  renderProperties,
  state,
  vp,
} from './core';
import {
  TABLE_IDS,
  type TableKey,
} from '../layers';
import { curLang } from './nameFields';
import {
  type SchedCellEdit,
  type SchedTableKind,
  type SchedTableLayout,
  scheduleTableHandles,
  scheduleTableLayout,
} from '../render';
import {
  cellEditorUi,
  ctx,
  HANDLE_PX,
} from '../main';

/** Each schedule table: the drawing field it is kept in (its layer id is
 *  `TABLE_IDS[field]`) and its name in the Parts tab. */
export const SCHED_TABLES: Record<SchedTableKind, { field: TableKey; label: string }> = {
  connection: { field: 'scheduleTable', label: 'Connection schedule' },
  flooring: { field: 'flooringTable', label: 'Flooring schedule' },
  lighting: { field: 'lightingTable', label: 'Lighting schedule' },
  pipe: { field: 'pipeTable', label: 'Pipe schedule' },
  preparation: { field: 'preparationTable', label: 'Preparation schedule' },
};
/** The tables in the order a press tries them. */
const SCHED_KINDS: SchedTableKind[] = ['connection', 'preparation', 'flooring', 'lighting', 'pipe'];
/** The table a layer id stands for. */
export const schedKindOf = (id: string): SchedTableKind | undefined => SCHED_KINDS.find((k) => schedTableId(k) === id);

/** The stored table object for a given kind. */
export function tableFor(kind: SchedTableKind): ScheduleTable | undefined {
  return state.drawing[SCHED_TABLES[kind].field];
}

/** Create each schedule table the first time its rows exist and the schedule is
 *  shown, anchored just to the right of the drawing content (the preparation
 *  table sits below the connection one). */
export function ensureScheduleTable(): void {
  if (!state.showSchedule) return;
  const hasConns = (state.drawing.connections || []).length > 0;
  const hasPreps = state.drawing.instances.some((i) => (i.preparations || []).length > 0);
  const hasFloors = (state.drawing.floorings || []).length > 0;
  const hasLights = state.drawing.instances.some((i) => (i.lights || []).length > 0);
  const hasPipes = (state.drawing.connections || []).some((c) => !!c.pipe);
  if ((!hasConns || state.drawing.scheduleTable) && (!hasPreps || state.drawing.preparationTable) && (!hasFloors || state.drawing.flooringTable)
    && (!hasLights || state.drawing.lightingTable) && (!hasPipes || state.drawing.pipeTable)) return;
  let maxX = -Infinity, maxY = -Infinity, minY = Infinity, has = false;
  for (const i of state.drawing.instances) {
    const r = Math.max(i.width, i.depth) / 2; // rotation-safe overestimate
    maxX = Math.max(maxX, i.x + r); maxY = Math.max(maxY, i.y + r); minY = Math.min(minY, i.y - r);
    has = true;
  }
  for (const f of state.drawing.floorings || []) for (const p of f.points) {
    maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); minY = Math.min(minY, p.y);
    has = true;
  }
  const x = has ? maxX + 500 : 0;
  const width = Math.max(2000, 380 / vp.scale);
  if (hasConns && !state.drawing.scheduleTable) {
    state.drawing.scheduleTable = { x, y: has ? maxY : 0, width };
  }
  if (hasPreps && !state.drawing.preparationTable) {
    // Place the preparation table below the connection one (or the content).
    state.drawing.preparationTable = { x, y: has ? minY - 500 : -3000, width };
  }
  if (hasFloors && !state.drawing.flooringTable) {
    // Below the preparation table (or the content).
    state.drawing.flooringTable = { x, y: has ? minY - 3500 : -6000, width };
  }
  if (hasLights && !state.drawing.lightingTable) {
    // Beside the connection table, on the content's top.
    state.drawing.lightingTable = { x: x + width + 500, y: has ? maxY : 0, width: width * 1.5 };
  }
  if (hasPipes && !state.drawing.pipeTable) {
    // Beside the connection table on the content's top (past the Lighting table).
    const lt = state.drawing.lightingTable;
    state.drawing.pipeTable = { x: lt ? lt.x + lt.width + 500 : x + width + 500, y: has ? maxY : 0, width: width * 2 };
  }
}

/** The layer model's stand-in id of a schedule table. */
export function schedTableId(kind: SchedTableKind): string {
  return TABLE_IDS[SCHED_TABLES[kind].field];
}

/** A schedule table's current on-screen layout (null when hidden/empty, or on
 *  a hidden layer — then it is not drawn). */
export function curScheduleLayout(kind: SchedTableKind): SchedTableLayout | null {
  const t = tableFor(kind);
  if (!t || !state.showSchedule || objHidden(schedTableId(kind))) return null;
  return scheduleTableLayout(ctx, vp, t, state.drawing, kind, drawingRev());
}

/** Which table's outer box a screen point falls inside (topmost first); a
 *  table on a locked layer can't be taken. */
export function scheduleTableBodyAt(sp: Vec2): SchedTableKind | null {
  for (const kind of SCHED_KINDS) {
    if (objUnpickable(schedTableId(kind))) continue;
    const L = curScheduleLayout(kind);
    if (L && sp.x >= L.originX && sp.x <= L.originX + L.widthPx && sp.y >= L.originY && sp.y <= L.originY + L.totalH) return kind;
  }
  return null;
}

/** The width-resize handle of the selected table at `sp`, if any. */
export function scheduleTableHandleAt(sp: Vec2): { kind: SchedTableKind; handle: -1 | 1 } | null {
  const kind = state.selectedScheduleTable;
  if (!kind) return null;
  const t = tableFor(kind);
  const L = curScheduleLayout(kind);
  if (!t || !L) return null;
  for (const h of scheduleTableHandles(vp, t, L.totalH)) {
    const s = vp.worldToScreen(h.world);
    if (Math.abs(s.x - sp.x) <= HANDLE_PX && Math.abs(s.y - sp.y) <= HANDLE_PX) return { kind, handle: h.sx as -1 | 1 };
  }
  return null;
}

/** The editable cell under a screen point (its table, row id, column, target). */
export function scheduleCellHitAt(sp: Vec2): { kind: SchedTableKind; rowId: string; col: number; edit: SchedCellEdit } | null {
  for (const kind of SCHED_KINDS) {
    if (objUnpickable(schedTableId(kind))) continue;
    const L = curScheduleLayout(kind);
    if (!L || sp.y < L.bodyY) continue;
    if (sp.x < L.originX || sp.x > L.originX + L.widthPx) continue;
    for (const row of L.rows) {
      const ry = L.originY + row.y;
      if (sp.y < ry || sp.y > ry + row.h) continue;
      for (let i = 0; i < row.cells.length; i++) {
        if (sp.x >= L.colX[i + 1] && sp.x <= L.colX[i + 2]) return { kind, rowId: row.id, col: i, edit: row.cells[i].edit };
      }
      return null;
    }
  }
  return null;
}

/** Screen rectangle of a given cell, for positioning the inline editor. */
function scheduleCellRect(kind: SchedTableKind, rowId: string, col: number): { x: number; y: number; w: number; h: number } | null {
  const L = curScheduleLayout(kind);
  if (!L) return null;
  const row = L.rows.find((r) => r.id === rowId);
  if (!row) return null;
  const ci = col + 1; // skip the No. column
  return { x: L.colX[ci], y: L.originY + row.y, w: L.colX[ci + 1] - L.colX[ci], h: row.h };
}

/** The base (un-numbered) name of a part (instance or polygon). */
function partBaseName(id: string): string {
  const inst = state.drawing.instances.find((i) => i.id === id);
  if (inst) return inst.enteredName ?? '';
  const poly = (state.drawing.polygons || []).find((p) => p.id === id);
  return poly?.name ?? '';
}

/** Name a part (instance or polygon) as entered (docs/claude/names.md): empty,
 *  its default again. A part's product names are left as they are. */
export function setPartName(id: string, value: string): void {
  const inst = state.drawing.instances.find((i) => i.id === id);
  if (inst) { if (value.trim()) inst.enteredName = value; else delete inst.enteredName; return; }
  const poly = (state.drawing.polygons || []).find((p) => p.id === id);
  if (poly) poly.name = value;
}

/** Find a preparation by id across all instances. */
export function findPreparation(id: string): Preparation | null {
  for (const inst of state.drawing.instances) {
    const p = (inst.preparations || []).find((pp) => pp.id === id);
    if (p) return p;
  }
  return null;
}

/** A connection's or a rough-in's label as the drawing shows it: its text, or —
 *  a product's — that label in the drawing's language. */
export function labelShown(x: { label?: string; labelI18n?: TextI18n } | null | undefined): string {
  return x ? shownName(undefined, x.labelI18n, curLang(), x.label ?? '') : '';
}
/** A label typed in the drawing: as typed, in every language (docs/claude/names.md §3). */
export function setTypedLabel(x: { label?: string; labelI18n?: TextI18n }, v: string): void {
  x.label = v;
  delete x.labelI18n;
}

/** Read the raw (editable) value backing a schedule cell edit. */
function cellEditValue(edit: SchedCellEdit): string {
  if (edit.kind === 'partName') return partBaseName(edit.id);
  if (edit.kind === 'connLabel') return labelShown(state.drawing.connections.find((c) => c.id === edit.id));
  if (edit.kind === 'prepLabel') return labelShown(findPreparation(edit.id));
  return ''; // 'none' — a read-only cell (flooring schedule)
}

/** Write a new value back to whatever a schedule cell edits. */
function applyCellEdit(edit: SchedCellEdit, value: string): void {
  if (edit.kind === 'partName') { setPartName(edit.id, value); return; }
  if (edit.kind === 'connLabel') {
    const c = state.drawing.connections.find((x) => x.id === edit.id);
    if (c) connEdit(c, { kind: 'label', text: value });
    return;
  }
  if (edit.kind !== 'prepLabel') return; // 'none' — read-only
  const p = findPreparation(edit.id);
  if (p) setTypedLabel(p, value);
}

/** Open the inline editor over a schedule-table cell. */
export function openCellEditor(kind: SchedTableKind, rowId: string, col: number, edit: SchedCellEdit): void {
  cellEditorUi.close(true);
  state.editingCell = { kind, rowId, col, edit };
  let look: EditorLook = {};
  cellEditorUi.open({
    text: cellEditValue(edit),
    // Over its cell; where the cell is gone for a frame, where it was.
    look: () => {
      const r = scheduleCellRect(kind, rowId, col);
      if (r) look = { left: `${r.x}px`, top: `${r.y}px`, width: `${Math.max(40, r.w)}px`, height: `${Math.max(24, r.h)}px` };
      return look;
    },
    keys: { enter: 'shift-newline', esc: 'cancels' },
    gesture: false,
    caret: 'all',
    commit: (t) => { const v = t.trim(); if (v !== cellEditValue(edit)) { pushHistory(); applyCellEdit(edit, v); } },
    closed: () => { state.editingCell = null; renderProperties(); invalidate(); },
  });
  invalidate();
}

/** Commit (or discard) the cell edit, writing back to the model. */
export function closeCellEditor(commit = true): void { cellEditorUi.close(commit); }
